import { motion } from "framer-motion";
import { COUPLE, COUPLE_PHOTO } from "@/lib/wedding";
import SmartImage from "@/components/SmartImage.jsx";

/**
 * Alliance
 * -----------------------------------------------------------
 * "The Sacred Alliance" - one shared couple photo flanked by
 * groom (left) and bride (right) details.
 */
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Alliance() {
  return (
    <section id="alliance" className="relative edge py-24 bg-ivory text-center">
      <div className="overline text-rani mb-5">Shubh Vivah</div>
      <h2 className="font-display text-4xl sm:text-6xl text-rani">The Sacred Alliance</h2>
      <div
        className="mx-auto mt-6 mb-14 h-px w-40"
        style={{ background: "linear-gradient(90deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.7) 50%, rgba(201,169,97,0) 100%)" }}
      />

      <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-10 lg:gap-16">
        <motion.div {...fade(0)} className="flex flex-col items-center md:col-start-2 md:row-start-1">
          <CouplePhoto />
        </motion.div>

        <div className="grid grid-cols-1 gap-14 md:contents">
          {/* Order matches the photo: Sonakshi stands on the left, Shubham on the right */}
          <motion.div {...fade(0.15)} className="md:col-start-1 md:row-start-1 min-w-0">
            <Person who={COUPLE.bride} rel="Daughter of" align="md:text-right md:items-end" />
          </motion.div>
          <motion.div {...fade(0.3)} className="md:col-start-3 md:row-start-1 min-w-0">
            <Person who={COUPLE.groom} rel="Son of" align="md:text-left md:items-start" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CouplePhoto() {
  return (
    <div className="relative w-[min(78vw,340px)] lg:w-[380px] rounded-t-full overflow-hidden border-2 border-gold/60 shadow-[0_30px_60px_rgba(58,10,24,0.25)] bg-bone">
      <div className="relative">
        <SmartImage
          src={COUPLE_PHOTO.src}
          alt={COUPLE_PHOTO.alt}
          label="Our photo"
          aspect="2/3"
          className="object-center"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "#7A1F3D", mixBlendMode: "lighten" }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(122,31,61,0.35), transparent 45%)" }}
        />
      </div>
      <div className="absolute inset-2 rounded-t-full border border-gold-light/50 pointer-events-none" />
    </div>
  );
}

function Person({ who, rel, align }) {
  return (
    <div className={`flex flex-col items-center text-center ${align}`}>
      <h3 className="font-display text-xl sm:text-2xl md:text-4xl text-rani uppercase tracking-widest break-words max-w-full">
        {who.name}
      </h3>
      <div className="mt-2 font-display italic text-sm md:text-base text-ink/70">{rel}</div>
      {/* one parent per line so both families wrap the same way */}
      <div className="mt-1 text-sm md:text-base font-semibold text-gold-deep leading-snug">
        {who.parents.split(" & ").map((p, i) => (
          <span key={p} className="block">{i > 0 ? "& " : ""}{p}</span>
        ))}
      </div>
      {who.grandparents?.length > 0 && (
        <>
          <div className="mt-5 font-display italic text-sm md:text-base text-ink/70">
            {who === COUPLE.bride ? "Granddaughter of" : "Grandson of"}
          </div>
          <ul className="mt-2 grid gap-2.5">
            {who.grandparents.map((g) => (
              <li key={g.relation} className="text-xs md:text-sm text-ink/80 leading-snug">
                <span className="block text-[10px] uppercase tracking-chip text-gold-deep">{g.relation}</span>
                {g.name}
                {g.note && <span className="block text-[11px] text-ink/60">{g.note}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="mt-4 inline-block border border-gold/50 rounded-full px-3 py-1 text-[10px] uppercase tracking-chip text-gold-deep">
        {who.city}
      </div>
    </div>
  );
}
